/**
 * D3 Canvas ↔ 编辑器桥接层(阶段一产出 → 可编辑区块 → 双形态提交)。
 *
 * 编辑期单一真相源 = form.props(现状 inspector/预览全复用);v2 content 在提交/落草稿时
 * 由 props 反推(有损截断仅影响 content.title 摘要,渲染真相仍在 props;dataPoints
 * 数字无损,bindTo 按 label 匹配原 dataPoints 保留 → D4 B 类精确渲染源不断链)。
 *
 * 派生 props 必须过 ai 侧逐样式严校验(extra=forbid + min_length):长度按 ai models.py
 * 上限截断。★D-12(2026-08-11)契约变更:派生**不再占位兜底** —— 「名称/内容」「99%」
 * 「在此填写文案」一旦成为真值,任何「值≠占位串」的闸都拦不住,最后被画进成品。
 * 空就是空;ai 的 min_length 由 buildCanvasCreatePayload 的拦空闸保证(空块拦在提交前)。
 */
import { isEmptySlots } from "./canvas";
import { slotSentence } from "./slotWords";
import type {
  BlockEmphasis,
  BlockScreenAnno,
  CanvasBlock,
  CanvasBlockContent,
  CanvasBlockImage,
  CanvasDataPoint,
  CanvasMeta,
  CanvasProduct,
  CanvasAsset,
  ImageSlots,
  LongImageCanvas,
  LongImageUserIntent,
  ProvenanceEntry,
  StyleReco,
  StyleTokens,
} from "./canvas";
import type { LongImageBlockForm } from "./blocks";
import {
  IMAGE_PROMPT_PLACEHOLDER,
  LongImageValidationError,
  STYLE_META,
  stripImageOnlyProps,
} from "./blocks";
import type { LongImageCreatePayload } from "../../api/posterService";
import type { GeneratedSnapshot } from "./staleBlocks";

/** 区块上的 v2 元数据(编辑器随 form 保存,提交时还原双形态)。 */
export interface CanvasBlockMeta {
  role: CanvasBlock["role"];
  renderMode: CanvasBlock["renderMode"];
  layout: CanvasBlock["layout"];
  /** D6 版面主次(D6 前存的草稿没这个字段 → 消费侧一律 `?? "normal"`)。 */
  emphasis?: BlockEmphasis;
  /** ★这一块对应用户大纲第几条(LI-ROUTE-1);老草稿没这一格 → `?? -1` = 整图级。 */
  outlineIndex?: number;
  images: CanvasBlockImage[];
  compliance: CanvasBlock["compliance"];
  styleOverride?: CanvasBlock["styleOverride"];
  /** 阶段一原始 dataPoints(bindTo 恢复源;编辑后按 label 匹配保留绑定)。 */
  planDataPoints: CanvasDataPoint[];
  /** ★光影三格还跟着整图风格走吗(批次③)。手改槽里任一格 → false,换画风不再动它。 */
  styleDerived?: boolean;
  /** ★排屏归属标注(LI-SCREENS-2.0 WP5)—— 见 `canvas.ts::BlockScreenAnno` 头注。 */
  screenAnno?: BlockScreenAnno | null;
  /** ★原件屏专用槽(LI-SCREENS-2.0)—— 见 `canvas.ts::CanvasBlock.originalKeys` 头注。 */
  originalKeys?: string[];
  /** ★原图直出块的本地预览 URL(LI30-B,client-only:提交序列化不含它,不进 wire)。 */
  originalPreviewUrl?: string;
  /** ★原件贴放形制(LI30-B,与 ai LongImageBlock.originalFit 成对):
   *  "full"=整宽无边框原样贴(直出块专用);缺省=grid 资质卡框网格。
   *  只有 addOriginalBlock 写它 —— 路由产的资质块永远不带,存量零变化。 */
  originalFit?: "grid" | "full";
}

export type CanvasBlockForm = LongImageBlockForm & { canvas?: CanvasBlockMeta };

/** styleTokens 微调 patch(palette 可只带改动色)。 */
export type StyleTokensPatch = Omit<Partial<StyleTokens>, "palette"> & {
  palette?: Partial<StyleTokens["palette"]>;
};

/** Canvas 模式编辑器状态(useCanvasBuilder 持有;草稿=本结构整包)。 */
export interface CanvasBuilderState {
  product: CanvasProduct;
  assets: CanvasAsset[];
  styleTokens: StyleTokens;
  blocks: CanvasBlockForm[];
  canvasMeta: CanvasMeta;
  /**
   * ★每条事实的出处(2026-08-01)。因为它在**状态里**,所以随草稿整包落
   * `xb_media_draft.form_json` —— 刷新后徽章还在,这正是"真落库不是内存"那条判据。
   *
   * 可选:老草稿没有这一项。缺 = 没读懂过 → 不画徽章(≠「来源不明」)。
   */
  provenance?: ProvenanceEntry[];
  /** ★数字白名单的真锚(原文)。缺 = 老草稿 → 改写侧回退用产品事实当锚。 */
  rawBlob?: string;
  /**
   * ★用户意图单里今天没有消费方的两格(ADV-LI-COPY-TABOO-1,2026-08-21):
   *   `copy` = 引号里的成品文案(原样印);`taboo` = 忌讳项(落出图 prompt 的否定段)。
   *   在**状态里**所以随草稿整包落 `xb_media_draft.form_json` —— 刷新后还在。
   *   缺 = 老稿/老路,**不是**"用户没写"(与 provenance 缺席同口径)。
   */
  userIntent?: LongImageUserIntent;
  /** ★骨架稿标记(2026-08-05):ai 规划判定,提交时原样透传给出图。 */
  skeleton?: boolean;
  /** ★整图风格单一来源(批次③):推荐 chip 的数据源。缺 = 老稿/老路 → 不画 chip。 */
  styleReco?: StyleReco | null;
  /**
   * ★每屏出图提示词只读预览(LI30-PV,0912):随 plan 产物进状态、随草稿整包落
   * form_json —— 不落这里,「查看出图提示词」只活在接稿那一次,二次恢复(formJson 路)
   * 就消失(0912 演示机走查实抓)。缺 = 老稿/预拼失败 → 屏卡不画入口。
   * 只进 UI 展示,提交契约不读它(buildCanvasCreatePayload 不发)。
   */
  screenPromptPreviews?: Array<{ screenNo: number; ratio?: string; prompt: string }>;
  /**
   * ★出图那一刻的逐块快照(2026-08-01,设计锚点 ⑤)。
   *
   * 提交成功后写进来,**并且不再清草稿** —— 用户从任务详情页「回搭建器改这一稿」
   * 回来时,靠它算出「哪一块改过了、图还是旧的」。
   * 缺 = 这份稿还没出过图(或老草稿)→ 不做任何提示。
   */
  generated?: GeneratedSnapshot;
}

const ROLE_NAME: Record<CanvasBlock["role"], string> = {
  HERO: "首屏主图",
  FEATURE: "卖点主张",
  SPEC: "规格参数",
  SCENE: "场景种草",
  CTA: "行动召唤",
  CONTRAST: "对比说服",
  TRUST: "信任背书",
  PRICE: "价格权益",
  INFO: "信息说明",
  QR: "二维码",
};

/**
 * 企业向用途的块名词面(2026-08-03 开卡批)。块名会渲染在编排列表与成图章节头上,
 * 公司简介顶着「卖点主张 / 场景种草」这类电商词就是错的。只列差异项,
 * 没列的回落默认表;非企业向用途走默认表,零回归。
 */
const ROLE_NAME_CORP: Partial<Record<CanvasBlock["role"], string>> = {
  HERO: "品牌主视觉",
  FEATURE: "核心业务",
  SPEC: "服务概览",
  SCENE: "场景风采",
  CTA: "联系我们",
  CONTRAST: "优势对比",
  TRUST: "实力背书",
  PRICE: "服务价格",
  INFO: "企业信息",
};

/**
 * 社交种草的块名词面(2026-08-04 social 批)。真实产物实证:种草单章节头渲染着
 * 「场景种草 / 卖点主张 / 行动召唤 / 价格权益」—— 公众号/小红书图文顶着电商叫卖词面
 * 就是错的(与 corp 那批同一课)。词面对齐 ai `arcs.py` 的 social 弧步名。
 */
const ROLE_NAME_SOCIAL: Partial<Record<CanvasBlock["role"], string>> = {
  HERO: "开场钩子",
  FEATURE: "种草要点",
  SPEC: "产品信息",
  SCENE: "细节实拍",
  CTA: "互动引导",
  CONTRAST: "前后对比",
  TRUST: "真实感受",
  PRICE: "价格参考",
  INFO: "小贴士",
  QR: "关注二维码",
};

/**
 * 活动海报的块名词面(2026-08-04 event 批)。真实产物实证:智能规划路的活动单章节头
 * 渲染「首屏主图 / 卖点主张 / 行动召唤」;词面对齐 ai `arcs.py` 的 event 弧步名
 * (区块搭建器模板路的词面本来就是这套,两条路从此一致)。
 */
const ROLE_NAME_EVENT: Partial<Record<CanvasBlock["role"], string>> = {
  HERO: "活动主视觉",
  FEATURE: "活动主题",
  SPEC: "亮点权益",
  SCENE: "现场氛围",
  CTA: "报名入口",
  CONTRAST: "参与流程",
  TRUST: "主办背书",
  PRICE: "票价优惠",
  INFO: "时间地点",
  QR: "报名二维码",
};

/**
 * 长尾六类目的块名词面(2026-08-04 全类目走查批)。词面全部对齐 ai `arcs.py` 各弧步名
 * (role→弧步 label;弧里没有的 role 补同语境词,未列项回落默认表)。
 */
const ROLE_NAME_REVIEW: Partial<Record<CanvasBlock["role"], string>> = {
  HERO: "测评封面",
  FEATURE: "测评结论",
  SPEC: "维度评分",
  SCENE: "上手体验",
  CTA: "选购建议",
  CONTRAST: "实测数据",
  TRUST: "优缺点对照",
  PRICE: "价格参考",
  INFO: "测评说明",
};

const ROLE_NAME_REPORT: Partial<Record<CanvasBlock["role"], string>> = {
  HERO: "战报封面",
  FEATURE: "核心结论",
  SPEC: "关键指标",
  SCENE: "现场实况",
  CTA: "下一步动作",
  CONTRAST: "同比对照",
  TRUST: "里程碑",
  INFO: "增长归因",
};

const ROLE_NAME_HIRE: Partial<Record<CanvasBlock["role"], string>> = {
  HERO: "雇主主视觉",
  FEATURE: "在招岗位",
  SPEC: "岗位职责",
  SCENE: "团队风采",
  CTA: "投递方式",
  CONTRAST: "成长路径",
  TRUST: "公司实力",
  PRICE: "薪酬福利",
  INFO: "工作地点",
  QR: "投递二维码",
};

const ROLE_NAME_MENU: Partial<Record<CanvasBlock["role"], string>> = {
  HERO: "门店主视觉",
  FEATURE: "招牌推荐",
  SPEC: "套餐组合",
  SCENE: "菜品实拍",
  CTA: "到店优惠",
  CONTRAST: "口味对照",
  TRUST: "口碑推荐",
  PRICE: "菜品价目",
  INFO: "门店信息",
  QR: "点单二维码",
};

const ROLE_NAME_TUTORIAL: Partial<Record<CanvasBlock["role"], string>> = {
  HERO: "教程封面",
  FEATURE: "分步骤操作",
  SPEC: "关键提示",
  SCENE: "步骤演示",
  CTA: "收藏引导",
  CONTRAST: "效果对照",
  TRUST: "真实反馈",
  INFO: "适用人群",
  QR: "关注二维码",
};

const ROLE_NAME_RANK: Partial<Record<CanvasBlock["role"], string>> = {
  HERO: "榜单封面",
  FEATURE: "逐名次条目",
  SPEC: "上榜理由",
  SCENE: "实拍展示",
  CTA: "结论与推荐",
  CONTRAST: "横向对照",
  TRUST: "数据来源",
  INFO: "评选口径",
};

const USAGE_ROLE_NAMES: Record<string, Partial<Record<CanvasBlock["role"], string>>> = {
  corp: ROLE_NAME_CORP,
  hire: ROLE_NAME_HIRE,
  social: ROLE_NAME_SOCIAL,
  event: ROLE_NAME_EVENT,
  review: ROLE_NAME_REVIEW,
  report: ROLE_NAME_REPORT,
  menu: ROLE_NAME_MENU,
  tutorial: ROLE_NAME_TUTORIAL,
  rank: ROLE_NAME_RANK,
};

/**
 * 块角色 → 章节头词面。
 *
 * ★2026-08-21 导出它,只为给跨仓契约锁调用:
 *   `__contract__/roleNames.contract.test.ts` ↔ ai `tests/poster/longimage/test_role_names_contract.py`。
 *   服务端(ai `longimage/role_names.py`)有这十张表的一份副本 —— 同一份表两个实现,
 *   而 console 这边改一个词面时,服务端那份**不会有任何地方报错**(成图章节头静默写错)。
 *   契约锁两侧各硬编码一次同样的 93 格,谁改谁当场红。
 */
export function roleNameOf(role: CanvasBlock["role"], useCase?: string | null): string {
  const table = useCase ? USAGE_ROLE_NAMES[useCase] : undefined;
  return table?.[role] ?? ROLE_NAME[role] ?? role;
}

// LI30(0910 拍板⑤):3 档 → 5 档,与 ai canvas_to_v1._BLOCK_RATIOS / models.blockRatio
// Literal 同集**同批放开**(单边放开会在提交时被 ai pydantic 422 拒)。
const BLOCK_RATIOS = new Set(["3:4", "1:1", "16:9", "4:5", "9:16"]);
/**
 * 真会把多张参考图一起画进一块的样式 —— 只有九宫格。
 * ★与 ai 侧 `longimage/models.py::MULTI_IMAGE_STYLES` 是同一份口径,改一边必须改另一边:
 *   这边多发一个样式的第 2 张图,那边不拼版,那张图照样上不了画面(还多算一笔上传)。
 */
const MULTI_IMAGE_STYLES = new Set(["grid9", "photocards"]);

/**
 * 值 + 单位 —— **值里已经带了单位就不再追加**(2026-08-05 真出图人眼核:「150kgkg」)。
 *
 * 读懂抽规格时,`value` 常常本来就是「150kg」而 `unit` 又填了「kg」(两边都对,拼一起就错)。
 * 这与 `_unit_suffix` 在标题上做的是同一件事,只是此前只做了标题那一半。
 */
const joinValueUnit = (dp: CanvasDataPoint) => {
  const value = dp.value.trim();
  const unit = dp.unit.trim();
  if (!unit) return value;
  if (value.endsWith(unit)) return value;
  // ★两边都是数字时直连会**造出一个假数字**(2026-08-21 测试机活动海报 e2e 实证):
  //   料「整箱 24 瓶 ¥55」→ 导演正确解析成 value=「¥55」/ unit=「24 瓶」,canvas 里数据是对的,
  //   而这里拼成「¥5524 瓶」,成品图上印着「整箱价格 ¥5524」。模型只是照着画,不是它画错。
  //   钱上的假数字,而且一声不响 —— 隔一个空格,宁可读着别扭也不许造数。
  //   原意图(value=150 / unit=kg → 150kg)不受影响:那条不是数字接数字。
  const wouldMergeDigits = /\d$/.test(value) && /^\d/.test(unit);
  return wouldMergeDigits ? `${value} ${unit}` : `${value}${unit}`;
};

const nonEmpty = (items: string[]) => items.map((s) => s.trim()).filter(Boolean);

/** dataPoints → {label,value} 行(pairs/table 共用;label 必填截断)。 */
function pairsFromDataPoints(dps: CanvasDataPoint[]): Array<{ label: string; value: string }> {
  return dps
    .filter((dp) => dp.label.trim())
    .map((dp) => ({ label: dp.label.trim().slice(0, 40), value: joinValueUnit(dp).slice(0, 120) }));
}

/**
 * content → props 派生(进编辑器/换排版时调用)。product 仅 spec 类兜底用
 * (阶段一 dataPoints 空但产品规格已填 → 参数表不空手)。
 */
export function derivePropsFor(
  blockV2: CanvasBlock,
  product?: CanvasProduct
): Record<string, unknown> {
  const meta = STYLE_META[blockV2.styleTag];
  const c = blockV2.content;
  const title = c.title.trim();
  const subtitle = c.subtitle.trim();
  const bullets = nonEmpty(c.bullets.map((b) => b.text));

  if (!meta) return {};

  if (blockV2.styleTag === "photocards") {
    /**
     * ★实拍分格卡(2026-08-05,对齐模板样图):props 形状与普通图块**不同** ——
     * 它吃 refUrl/refUrls + cards(每格的标题+说明),文字由 ai 侧矢量渲染,不进出图提示词。
     * 卡片文案来源:content.bullets(规划写的每格说明),标题优先取 `标题|说明` 的前半。
     */
    const cards = bullets.slice(0, 4).map((line) => {
      const [t, d] = line.split(/[|｜]/, 2);
      return { title: (d ? t : line).slice(0, 24).trim(), desc: (d ?? "").slice(0, 80).trim() };
    });
    const keys = blockV2.images.map((x) => x.objectKey);
    return {
      ...(keys[0] ? { refKey: keys[0] } : {}),
      ...(keys.length > 1 ? { refKeys: keys.slice(1) } : {}),
      cards,
      ...(title ? { heading: title.slice(0, 24) } : {}),
      blockRatio: "3:4",
    };
  }

  if (meta.primitive === "image") {
    const img = blockV2.images[0];
    /**
     * ★★这里是「57% 的画面描述其实是文案」的真身(2026-08-02,条 3)。
     *
     * 拆槽之前,图块的画面描述就是 `[title, subtitle, ...bullets].join(";")` ——
     * 也就是把这块的**营销文案**原样当成 prompt 发给出图引擎。
     * 老抱怨「引擎会把文案画进图」因此不是引擎的问题:文案本来就是 prompt,
     * 而下面 `overlayText` 还会把标题再说一遍,同一句话在提示词里出现两次。
     *
     * 现在:规划给了 `scene`(五格 + 限定自由口)→ prompt 由**结构化事实 + 枚举**派生;
     * 没给 → 逐字走老路(存量草稿 / 模板 / 蓝图 / 场景卡 / 横宽四条入口零回归)。
     * ★`prompt` 这一格没有退休:它要显示给用户看,也是 `blockDigest` 的一部分;
     *   ai 侧编译真正消费的是 `slots`(见 `orchestrator._slot_prompt_parts`)。
     */
    /**
     * ★三个自由文本口钳到 120(2026-08-06 线上 422)。
     *
     * 用户点「确认出图」直接吃到
     * `POSTER_INVALID_PARAMS 长图请求被拒:blocks.3.slots.subject —— at most 120 characters`,
     * **整份产物被一个格子否掉**(第四次踩这条,见 feedback-one-bad-field-must-not-void-the-whole-payload)。
     *
     * ai `ImageSlots.subject/environment/extra` 三个自由口都是 `max_length=120`(枚举格不受限)。
     * 这条组包边界上别的字段都钳过了 —— prompt 1200、overlayText 40、
     * overlayLines「≤14 字 ≤3 条、坏行丢行不丢块」—— 唯独 slots 是 08-05 拆槽时新加的,漏了钳子。
     * ★上游还有一处**口径歪的**已一并改:`BlockInspector` 的一段话主控 `maxLength` 写着 300,
     *   而它绑的就是这个 120 的 subject(D3 把主控从 prompt(1200)改绑 slots 时只改了一边)。
     * ★这里仍然要钳,不能只靠上游:存量草稿里已经躺着 >120 的值(报障那份就是),
     *   光把输入框改成 120 救不回已经存下的稿子。
     */
    const clampSlots = (s: ImageSlots): ImageSlots => ({
      ...s,
      ...(s.subject ? { subject: s.subject.slice(0, 120) } : {}),
      ...(s.environment ? { environment: s.environment.slice(0, 120) } : {}),
      ...(s.extra ? { extra: s.extra.slice(0, 120) } : {}),
    });
    const slots = isEmptySlots(blockV2.scene)
      ? null
      : clampSlots(blockV2.scene as unknown as ImageSlots);
    const promptParts = slots ? slotSentence(slots) : nonEmpty([title, subtitle, ...bullets]);
    const prompt =
      promptParts.join(";").slice(0, 1200) ||
      `突出${ROLE_NAME[blockV2.role] ?? "产品"}的画面,主体清晰、质感真实`;
    const i2iOn = !!img?.i2i.enabled;
    /**
     * ★2026-07-30:多图样式(grid9)要把第 2..N 张也发下去。
     *
     * 此前这里只取 `images[0]`,而规划提示词写着「同类多图可合进一个多图块」——
     * 8 图真跑的结果是规划把 8 张分给 4 块、实际只有 4 张进了画面。
     * 单图样式**保持只发首图**(它们物理上也只画一张),多余的图由 ai 侧补齐层
     * 安置到别的块;grid9 由 ai 侧拼版成一张 init 图进 image_edit。
     */
    const extraKeys =
      i2iOn && MULTI_IMAGE_STYLES.has(blockV2.styleTag)
        ? blockV2.images.slice(1).map((x) => x.objectKey)
        : [];
    /**
     * ★卖点短语上图(2026-08-04 批② T1):ai 解析层已过逐字门,这里只透传 + 形状钳
     * (与 ai `ImageProps.overlayLines` 同口径:≤3 条、≤14 字,坏行丢行不丢块)。
     * 此前图块只带 title[:40] 当叠字 —— 上图的卖点在提交那一步整个断链。
     */
    const overlayLines = (blockV2.overlayLines ?? [])
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && s.length <= 14)
      .slice(0, 3);
    return {
      genMode: i2iOn ? "i2i" : "t2i",
      ...(i2iOn && img ? { refKey: img.objectKey } : {}),
      ...(extraKeys.length ? { refKeys: extraKeys } : {}),
      // VLM 早就看出来的两条事实,这里才第一次真的传到出图那一步
      ...(i2iOn && img?.hasText ? { refHasText: true } : {}),
      ...(i2iOn && img?.textSafe && img.textSafe !== "none" ? { refTextSafe: img.textSafe } : {}),
      prompt,
      ...(slots ? { slots } : {}),
      ...(title ? { overlayText: title.slice(0, 40) } : {}),
      ...(overlayLines.length ? { overlayLines } : {}),
      // D6 主次:上传图自带比例优先;没有就按主次给画幅 —— 次要图块压成 16:9,
      // 别让「补充说明」的配图跟首屏主图一样占满一屏(hero/normal 维持存量 3:4)。
      blockRatio:
        img && BLOCK_RATIOS.has(img.ratio)
          ? img.ratio
          : (blockV2.emphasis ?? "normal") === "minor"
            ? "16:9"
            : "3:4",
    };
  }

  switch (blockV2.styleTag) {
    case "numlist":
    case "twocol": {
      const items = bullets.map((s) => s.slice(0, 120)).slice(0, 12);
      // D-12:规划没给内容就给空,**不塞占位串当 value** —— 「第一条/第二条」是真值,
      // 任何「值≠占位串」的闸都拦不住它,最后被原样画进成品。
      return {
        ...(title ? { title: title.slice(0, 40) } : {}),
        items,
      };
    }
    case "cta": {
      const text = (c.cta.trim() || title || subtitle || "立即了解 →").slice(0, 600);
      return { text, bg: "accent" };
    }
    case "cite": {
      // D-12:同上,空就是空 —— 占位话术一旦成为真值就再也拦不住
      const text = (title || subtitle || bullets[0] || "").slice(0, 600);
      return { text, ...(subtitle && title ? { source: subtitle.slice(0, 60) } : {}) };
    }
    case "bigtext":
    case "golden":
    case "body": {
      const text = (title || subtitle || bullets.join(";") || "").slice(0, 600);
      const sub = title && subtitle ? subtitle.slice(0, 200) : undefined;
      return { text, ...(sub ? { sub } : {}) };
    }
    case "stat":
    case "percent":
    case "beforeafter": {
      const items = c.dataPoints
        .filter((dp) => dp.label.trim() && joinValueUnit(dp))
        .map((dp) => ({
          value: joinValueUnit(dp).slice(0, 20),
          label: dp.label.trim().slice(0, 30),
        }))
        .slice(0, 6);
      return {
        ...(title ? { title: title.slice(0, 40) } : {}),
        items,
      };
    }
    case "comparebar":
    case "line":
    case "pie": {
      const series = c.dataPoints
        .filter((dp) => dp.label.trim())
        .map((dp, i) => {
          const n = Number.parseFloat(dp.value);
          return {
            label: dp.label.trim().slice(0, 30),
            value: Number.isFinite(n) ? n : 0,
            highlight: blockV2.styleTag === "comparebar" && i === 0,
          };
        })
        .slice(0, 12);
      const unit = c.dataPoints.find((dp) => dp.unit.trim())?.unit.trim();
      return {
        ...(title ? { title: title.slice(0, 40) } : {}),
        ...(unit ? { unit: unit.slice(0, 12) } : {}),
        series,
      };
    }
    case "table": {
      const rows = c.dataPoints
        .filter((dp) => dp.label.trim())
        .map((dp) => [dp.label.trim().slice(0, 60), joinValueUnit(dp).slice(0, 60)])
        .slice(0, 20);
      return {
        ...(title ? { title: title.slice(0, 40) } : {}),
        headers: ["项目", "参数"],
        rows,
      };
    }
    case "rating": {
      const items = c.dataPoints
        .filter((dp) => dp.label.trim())
        .map((dp) => {
          const n = Number.parseFloat(dp.value);
          return {
            label: dp.label.trim().slice(0, 30),
            score: Math.max(0, Math.min(5, Number.isFinite(n) ? n : 0)),
          };
        })
        .slice(0, 8);
      return {
        ...(title ? { title: title.slice(0, 40) } : {}),
        scale: 5,
        items,
      };
    }
    case "qr": {
      const cta = c.cta.trim();
      return {
        content: /^https?:\/\//.test(cta) ? cta.slice(0, 512) : "https://",
        caption: (title || "扫码了解更多").slice(0, 60),
      };
    }
    default: {
      // pairs 家族(spec/infocard/price/rank/timeline/flow/sales/honor/media)
      let items = pairsFromDataPoints(c.dataPoints).slice(0, 16);
      if (!items.length && blockV2.styleTag === "spec" && product) {
        items = pairsFromDataPoints(product.specs.map((s) => ({ ...s, bindTo: null }))).slice(
          0,
          16
        );
      }
      return {
        ...(title ? { title: title.slice(0, 40) } : {}),
        items,
      };
    }
  }
}

/** CanvasBlock → 编辑器 form(id=blockId:子表 uk / 单块重做 / 分段回调三方对齐)。 */
export function canvasBlockToForm(
  blockV2: CanvasBlock,
  product?: CanvasProduct,
  useCase?: string | null
): CanvasBlockForm {
  const meta = STYLE_META[blockV2.styleTag];
  return {
    id: blockV2.blockId,
    primitive: meta.primitive,
    styleTag: blockV2.styleTag,
    name: roleNameOf(blockV2.role, useCase).slice(0, 40),
    props: derivePropsFor(blockV2, product),
    canvas: {
      role: blockV2.role,
      renderMode: blockV2.renderMode,
      layout: blockV2.layout,
      emphasis: blockV2.emphasis ?? "normal",
      outlineIndex: blockV2.outlineIndex ?? -1,
      images: blockV2.images,
      compliance: blockV2.compliance,
      styleOverride: blockV2.styleOverride ?? null,
      planDataPoints: blockV2.content.dataPoints,
      styleDerived: blockV2.styleDerived ?? false,
      screenAnno: blockV2.screenAnno ?? null,
      originalKeys: blockV2.originalKeys ?? [],
    },
  };
}

/** 阶段一 Canvas → 编辑器状态(blocks 按 order 稳定排序)。 */
export function canvasToBuilderState(canvas: LongImageCanvas): CanvasBuilderState {
  const ordered = [...canvas.blocks].sort((a, b) => a.order - b.order);
  return {
    product: canvas.product,
    assets: canvas.assets,
    styleTokens: canvas.styleTokens,
    blocks: ordered.map((b) => canvasBlockToForm(b, canvas.product, canvas.canvasMeta?.useCase)),
    canvasMeta: canvas.canvasMeta,
    // ★出处与原文锚从 Canvas 搬进编辑器状态 —— 漏了这两行,ai 侧落了库前端也看不见。
    provenance: canvas.provenance ?? [],
    rawBlob: canvas.rawBlob ?? "",
    // ★与 provenance/rawBlob 同一条理由:漏了这一行,ai 侧算出来了前端也看不见,
    //   而且提交时又原样丢回去 —— 用户写的忌讳和逐字文案就此蒸发,一声不吭。
    userIntent: canvas.userIntent,
    skeleton: canvas.skeleton ?? false,
    styleReco: canvas.styleReco ?? null,
    // ★LI30-PV:与 provenance 同一条理由 —— 漏了这一行,ai 预拼了前端只在接稿那一刻看得见。
    ...(canvas.screenPromptPreviews?.length
      ? { screenPromptPreviews: canvas.screenPromptPreviews }
      : {}),
  };
}

// ── props → content 反推(提交/落草稿)─────────────────────────────────────────

const emptyContent = (): CanvasBlockContent => ({
  title: "",
  subtitle: "",
  bullets: [],
  dataPoints: [],
  cta: "",
});

function dp(label: string, value: string, unit = ""): CanvasDataPoint {
  return {
    label: label.slice(0, 40),
    value: value.slice(0, 120),
    unit: unit.slice(0, 12),
    bindTo: null,
  };
}

/** 编辑后 props → v2 content(dataPoints 数字无损;文本仅入 title/subtitle 摘要)。 */
export function contentFromProps(
  styleTag: string,
  props: Record<string, unknown>,
  planDataPoints: CanvasDataPoint[] = []
): CanvasBlockContent {
  const meta = STYLE_META[styleTag];
  const c = emptyContent();
  if (!meta) return c;
  const s = (v: unknown) => (typeof v === "string" ? v : "");

  if (meta.primitive === "image") {
    c.title = s(props.overlayText).slice(0, 60);
    return c;
  }

  switch (styleTag) {
    case "numlist":
    case "twocol":
      c.title = s(props.title).slice(0, 60);
      c.bullets = (Array.isArray(props.items) ? (props.items as unknown[]) : [])
        .map((x) => ({ text: s(x).slice(0, 120) }))
        .filter((b) => b.text);
      return c;
    case "cta":
      c.cta = s(props.text).slice(0, 40);
      return c;
    case "bigtext":
    case "golden":
    case "body":
    case "cite":
      c.title = s(props.text).slice(0, 60);
      c.subtitle = (s(props.sub) || s(props.source)).slice(0, 120);
      return c;
    case "stat":
    case "percent":
    case "beforeafter":
      c.title = s(props.title).slice(0, 60);
      c.dataPoints = (Array.isArray(props.items) ? (props.items as unknown[]) : [])
        .map((x) => {
          const it = (x ?? {}) as { label?: unknown; value?: unknown };
          return dp(s(it.label), s(it.value));
        })
        .filter((x) => x.label);
      break;
    case "comparebar":
    case "line":
    case "pie": {
      c.title = s(props.title).slice(0, 60);
      const unit = s(props.unit);
      c.dataPoints = (Array.isArray(props.series) ? (props.series as unknown[]) : [])
        .map((x) => {
          const it = (x ?? {}) as { label?: unknown; value?: unknown };
          return dp(s(it.label), String(typeof it.value === "number" ? it.value : 0), unit);
        })
        .filter((x) => x.label);
      break;
    }
    case "table":
      c.title = s(props.title).slice(0, 60);
      c.dataPoints = (Array.isArray(props.rows) ? (props.rows as unknown[]) : [])
        .map((r) => {
          const row = Array.isArray(r) ? (r as unknown[]).map(s) : [];
          return dp(row[0] ?? "", row.slice(1).join(" / "));
        })
        .filter((x) => x.label);
      break;
    case "rating":
      c.title = s(props.title).slice(0, 60);
      c.dataPoints = (Array.isArray(props.items) ? (props.items as unknown[]) : [])
        .map((x) => {
          const it = (x ?? {}) as { label?: unknown; score?: unknown };
          return dp(s(it.label), String(typeof it.score === "number" ? it.score : 0));
        })
        .filter((x) => x.label);
      break;
    case "qr":
      c.cta = s(props.content).slice(0, 40);
      c.title = s(props.caption).slice(0, 60);
      return c;
    default:
      // pairs 家族
      c.title = s(props.title).slice(0, 60);
      c.dataPoints = (Array.isArray(props.items) ? (props.items as unknown[]) : [])
        .map((x) => {
          const it = (x ?? {}) as { label?: unknown; value?: unknown };
          return dp(s(it.label), s(it.value));
        })
        .filter((x) => x.label);
      break;
  }
  // bindTo 按 label 匹配阶段一 dataPoints 恢复(产品信息联动链 D4 用,不因编辑断掉)
  for (const point of c.dataPoints) {
    const orig = planDataPoints.find((o) => o.label === point.label);
    if (orig && orig.bindTo != null) point.bindTo = orig.bindTo;
  }
  return c;
}

// ── 提交双形态 ────────────────────────────────────────────────────────────────

/**
 * Canvas 编辑态 → createTask.longImage 整包:
 * v1 键喂现状渲染管线(ai LongImageBlock extra=ignore 吞掉 v2 键),
 * v2 键由 backend replaceForTask 落 xb_detail_long_block 子表。
 */
/** 意图单是不是真有内容 —— 两格都空等于没有(空对象也不发,免得两侧键集打架)。 */
function hasUserIntent(ui: LongImageUserIntent | undefined): ui is LongImageUserIntent {
  return !!ui && ((ui.copy?.length ?? 0) > 0 || (ui.taboo?.length ?? 0) > 0);
}

export function buildCanvasCreatePayload(
  state: CanvasBuilderState,
  style?: { key: string; label: string; tone?: string } | null,
  /** 0831 双档:页面选的出图档(OPENAI/OPENAI_VIP)。canvas state 不持 provider,由页面注入。 */
  provider: string = "OPENAI"
): LongImageCreatePayload {
  if (state.blocks.length === 0) throw new Error("至少需要 1 个区块");
  // ★闸错误必须带 issues(0817 走查「确认出图零反馈」定性三层剥完的真相):
  //   此前这里抛裸 Error → 页面 onError 只弹一条**几秒自灭的 toast**,常驻红清单
  //   BlockIssuesPanel 因拿不到 issues 不渲染 —— 用户一错过 toast,按钮看起来就是
  //   「点了没反应」(laoban1 与 v46 双复现)。手动路(blocks.ts)从 7.29 起就带清单,
  //   canvas 路漏了。另:逐块**收全再抛**,别修一块撞一块。
  const issues: Array<{ blockId: string; blockName: string; message: string }> = [];
  for (const b of state.blocks) {
    if (b.primitive === "image") {
      // ★实拍分格卡没有 `prompt`(2026-08-05 本地真跑抓到:点「确认出图」静默不提交)——
      //   它的内容是 cards + 实拍图,画面不由提示词决定。这道闸问的是「这块出得出来吗」,
      //   对它就该问「有没有图」,拿别的样式的字段去问它等于永远判它不合格。
      if (b.styleTag === "photocards") {
        const props = b.props as { refKey?: unknown; refKeys?: unknown };
        const n =
          (props.refKey ? 1 : 0) + (Array.isArray(props.refKeys) ? props.refKeys.length : 0);
        if (n === 0) {
          issues.push({
            blockId: b.id,
            blockName: b.name,
            message: "实拍分格卡还没有选照片 —— 选上照片,或换成 AI 出图的样式/删掉这一块",
          });
        }
        continue;
      }
      const prompt = String((b.props as { prompt?: unknown }).prompt ?? "").trim();
      // D-12:V2 提交路此前只拦空串 —— 占位串「在此描述画面…」非空,原样送去出图。
      // 与手动路(blocks.ts validateDraft)同判据:字面等于占位串 = 没填。
      if (!prompt || prompt === IMAGE_PROMPT_PLACEHOLDER) {
        issues.push({ blockId: b.id, blockName: b.name, message: "还没有画面描述" });
      }
      continue;
    }
    // D-12 拦空闸:派生不再占位兜底(见文件头),空内容在这里拦下 ——
    // 比 ai 的 min_length 422 更早、话更明白;不拦就等于把空块白白送去计费。
    const p = b.props as {
      text?: unknown;
      items?: unknown[];
      series?: unknown[];
      rows?: unknown[];
    };
    const emptyText = typeof p.text === "string" && !p.text.trim();
    const emptyList = (["items", "series", "rows"] as const).some(
      (k) => Array.isArray(p[k]) && (p[k] as unknown[]).length === 0
    );
    if (emptyText || emptyList) {
      issues.push({
        blockId: b.id,
        blockName: b.name,
        message: "还没有内容 —— 填上文案/数据再出图,或删掉这一块",
      });
    }
  }
  if (issues.length) {
    // message 里保留逐条话术 —— toast/老消费方读的是 message,清单消费方读 issues
    throw new LongImageValidationError(
      `还差 ${issues.length} 处才能出图:` +
        issues.map((i) => `「${i.blockName}」${i.message}`).join(";"),
      issues
    );
  }
  return {
    canvas: {
      direction: "v",
      width: state.canvasMeta.targetWidth,
      scale: state.canvasMeta.scale,
      background: "white",
      useCase: state.canvasMeta.useCase,
      // ★骨架稿透传(2026-08-05):图块在 ai 侧改画示意位,不下引擎不计费
      skeleton: state.skeleton ?? false,
    },
    // ★带上用户选的画风(2026-08-01 面板合一)。这个槽 ai 一直在读
    //   (`orchestrator` 的 `style_tone` → 图块 prompt 的【全局风格】段),
    //   只是智能稿这条路以前从没往里放过东西 —— 那不是产品决策,是接线欠账。
    style: style ?? { key: "canvas_auto", label: "智能规划" },
    // ★generated Provider union 还没有 OPENAI_VIP,cast 过桥(手动路 blocks.ts:731 同法)
    provider: provider as LongImageCreatePayload["provider"],
    // ★ADV-LI-COPY-TABOO-1:用户写的成品文案与忌讳,**非空才发**。
    //   无条件发会打破与 ai `canvas_to_v1.py` 的逐格对拍(那条 diff 是 `set(py) | set(ts)`,
    //   任一侧多一个键就报「另一侧缺」)。两侧同口径,谁都不将就。
    //   ★这一格此前**只加在了服务端那份投影上** —— 而用户在页面点「一键出图」走的是
    //     这里(服务端那份是给军师/定时任务这类无头调用方用的)。只修那边等于没修,
    //     且逐格对拍**看不出来**(两边都不发 ⇒ 逐格相同 ⇒ 绿),
    //     所以另有一条「能力对拍」钉住"两边都得有这一格"。
    ...(hasUserIntent(state.userIntent) ? { userIntent: state.userIntent } : {}),
    styleTokens: state.styleTokens,
    blocks: state.blocks.map((b, i) => {
      const meta = b.canvas;
      return {
        id: b.id,
        primitive: b.primitive,
        styleTag: b.styleTag,
        name: b.name,
        // 图键不落非图块(0917,与手动路 blocks.ts 同一份判据)
        props: stripImageOnlyProps(b.primitive, b.props),
        ...(meta
          ? {
              blockId: b.id,
              order: i,
              role: meta.role,
              renderMode: meta.renderMode,
              layout: meta.layout,
              emphasis: meta.emphasis ?? "normal",
              // ★C1(LI-ROUTE-1):块的**段号**,出图时按它过滤只属于某一段的用户要求。
              //   老草稿没这一格 → -1 = 整图级 ⇒ 退回广播,与加这一格之前逐字相同。
              outlineIndex: meta.outlineIndex ?? -1,
              content: contentFromProps(b.styleTag, b.props, meta.planDataPoints),
              images: meta.images,
              compliance: meta.compliance,
              ...(meta.styleOverride ? { styleOverride: meta.styleOverride } : {}),
              // ★排屏标注/原件槽逐格对拍(ai canvas_to_v1.py 同款「有才发」,见 canvas.ts
              //   BlockScreenAnno 头注):老草稿/手工搭建器的块两格皆缺,不发这两个键,
              //   与逐格对拍 `set(py) | set(ts)` 的既有纪律一致(D-12 style/userIntent 同款教训)。
              ...(meta.screenAnno ? { screenAnno: meta.screenAnno } : {}),
              ...(meta.originalKeys?.length
                ? {
                    originalKeys: meta.originalKeys,
                    ...(meta.originalFit ? { originalFit: meta.originalFit } : {}),
                  }
                : {}),
            }
          : {}),
      };
    }),
  };
}

// ── 草稿 serialize/parse(服务器 formJson + localStorage 镜像共用)────────────

const DRAFT_KIND = "longimage_canvas_v1";

interface PersistedCanvasDraft extends CanvasBuilderState {
  kind: typeof DRAFT_KIND;
  savedAt: string;
}

export function serializeCanvasDraft(state: CanvasBuilderState): string {
  const persisted: PersistedCanvasDraft = {
    kind: DRAFT_KIND,
    savedAt: new Date().toISOString(),
    ...state,
  };
  return JSON.stringify(persisted);
}

/**
 * 「这份 /poster/drafts 草稿是不是长图智能稿」—— 只认外壳 kind 标记,不做全量验型
 * (验型是 parseCanvasDraft 的事;分流判据要的是便宜和稳定)。
 * 作品页「我的草稿」分流用(2026-08-10 用户报障:智能稿与自由创作向导共用一个草稿存储,
 * 此前一律送进 /ai-haibao/new 六步向导 —— 智能稿点「继续编辑」落进自由创作,
 * 向导读不懂 formJson,看起来就是「草稿没存」)。
 */
export function isCanvasDraftRaw(raw: string): boolean {
  try {
    return (JSON.parse(raw) as { kind?: unknown }).kind === DRAFT_KIND;
  } catch {
    return false;
  }
}

/**
 * ★异步作业锚的**空壳草稿**(0910 深夜「草稿跳错页」修):一键/编排提交前 ensureDraft
 * 拿 draftId 时,备料态还没有 CanvasBuilderState 可序列化 —— 旧实现存 `"{}"`(只为过
 * MySQL JSON 列),`isCanvasDraftRaw` 判 false → 作品页把它分流进 /ai-haibao/new
 * 六步向导 → 向导读不懂 formJson → 踢回创建首页(0910 老板真机两连踩)。
 * 壳带 kind(分流回搭建器)+ useCase(恢复扫描的用途闸认得,一键在途稿不串卡)。
 * parseCanvasDraft 对它返回 null(无 blocks 验型不过)—— rider①「空壳不弹横幅」不变。
 */
export function serializeEmptyDraftShell(
  useCase?: string | null,
  opts?: { oneclick?: boolean; styleKey?: string | null }
): string {
  return JSON.stringify({
    kind: DRAFT_KIND,
    savedAt: new Date().toISOString(),
    useCase,
    // ★1006 复测:企业介绍稿编排后从草稿卡回来,备料页画风变「AI 定」—— 用户在备料页选的
    //   画风只活在组件 state 里。壳里记一份画风 key,恢复时还给备料页(draftShellStyleKey)。
    ...(opts?.styleKey ? { styleKey: opts.styleKey } : {}),
    // ★一键标(0911 老板拍板「一键不用所谓的保存草稿,直接处理中」):作品页草稿列表
    //   据此把一键的作业锚壳**不出卡**——用户面一键只有「处理中」,没有草稿概念。
    //   编排壳不带此标(编排完成后用户要靠草稿卡回来接稿,藏了就断回路)。
    ...(opts?.oneclick ? { oneclick: true } : {}),
  });
}

/** 一键作业锚壳判定(作品页草稿列表过滤用;非壳/编排壳/真稿 → false)。 */
export function isOneClickShellRaw(raw: string): boolean {
  try {
    const d = JSON.parse(raw) as { kind?: unknown; oneclick?: unknown; blocks?: unknown };
    return d.kind === DRAFT_KIND && d.oneclick === true && !Array.isArray(d.blocks);
  } catch {
    return false;
  }
}

/** 壳里记的备料页画风 key(1006);没有/坏 JSON = null。 */
export function draftShellStyleKey(raw: string): string | null {
  try {
    const d = JSON.parse(raw) as { kind?: unknown; styleKey?: unknown };
    if (d.kind !== DRAFT_KIND) return null;
    return typeof d.styleKey === "string" && d.styleKey ? d.styleKey : null;
  } catch {
    return null;
  }
}

/** 空壳/正稿通用的外壳用途读取(恢复扫描用途闸用;判不出 = null)。 */
export function draftShellUseCase(raw: string): string | null {
  try {
    const d = JSON.parse(raw) as { kind?: unknown; useCase?: unknown; canvasMeta?: unknown };
    if (d.kind !== DRAFT_KIND) return null;
    if (typeof d.useCase === "string" && d.useCase) return d.useCase;
    const meta = d.canvasMeta as { useCase?: unknown } | undefined;
    return typeof meta?.useCase === "string" && meta.useCase ? meta.useCase : null;
  } catch {
    return null;
  }
}

const HEX = /^#[0-9A-Fa-f]{6}$/;
const PALETTE_KEYS = ["primary", "secondary", "accent", "ink", "muted", "line", "bg"] as const;

/** 逐字段验型:旧版本/被污染草稿整包丢弃(绝不带病进编辑器白屏)。 */
export function parseCanvasDraft(raw: string): CanvasBuilderState | null {
  try {
    const d = JSON.parse(raw) as PersistedCanvasDraft;
    if (d.kind !== DRAFT_KIND) return null;
    if (!Array.isArray(d.blocks) || d.blocks.length === 0) return null;
    for (const b of d.blocks) {
      if (!b || typeof b !== "object") return null;
      const meta = STYLE_META[String(b.styleTag)];
      if (!meta || meta.primitive !== b.primitive) return null;
      if (!b.id || typeof b.props !== "object" || b.props === null) return null;
      if (b.canvas != null && (typeof b.canvas !== "object" || !Array.isArray(b.canvas.images)))
        return null;
    }
    const pal = d.styleTokens?.palette;
    if (!pal || PALETTE_KEYS.some((k) => !HEX.test(String(pal[k])))) return null;
    if (!d.product || typeof d.product !== "object" || !Array.isArray(d.assets)) return null;
    const cm = d.canvasMeta;
    if (!cm || typeof cm.targetWidth !== "number" || ![1, 2, 3].includes(Number(cm.scale)))
      return null;
    return {
      product: d.product,
      assets: d.assets,
      styleTokens: d.styleTokens,
      blocks: d.blocks,
      canvasMeta: cm,
      // ★这两行就是「刷新后徽章还在」那条判据的落点。
      //   本函数是**逐字段重建**的(不是整包透传),不写在这里 = 存进去了、读不回来。
      //   老草稿没有这两项 → 逐条验型后归空,不整包丢弃(老稿必须照常进编辑器)。
      provenance: parseProvenanceList(d.provenance),
      rawBlob: typeof d.rawBlob === "string" ? d.rawBlob : "",
      // ★出图快照同理:不写在这里 = 刷新一次「图还是旧的」就再也算不出来了。
      ...(parseGenerated(d.generated) ? { generated: parseGenerated(d.generated)! } : {}),
      // ★推荐 chip 同理(本函数是逐字段重建):不写 = 存了、刷新读不回来。坏形状归 null。
      styleReco: parseStyleReco(d.styleReco),
      // ★LI30-PV 提示词预览同理:坏形状逐条丢、全空归缺席(缺席 = 不画入口,不是空面板)。
      ...(() => {
        const pv = parseScreenPromptPreviews(d.screenPromptPreviews);
        return pv.length ? { screenPromptPreviews: pv } : {};
      })(),
    };
  } catch {
    return null;
  }
}

/** LI30-PV 提示词预览验型:逐条收,screenNo 数字 + prompt 非空串才算一条;其余丢。 */
function parseScreenPromptPreviews(
  v: unknown
): Array<{ screenNo: number; ratio?: string; prompt: string }> {
  if (!Array.isArray(v)) return [];
  const out: Array<{ screenNo: number; ratio?: string; prompt: string }> = [];
  for (const it of v) {
    if (!it || typeof it !== "object") continue;
    const { screenNo, ratio, prompt } = it as {
      screenNo?: unknown;
      ratio?: unknown;
      prompt?: unknown;
    };
    if (typeof screenNo !== "number" || typeof prompt !== "string" || !prompt.trim()) continue;
    out.push({
      screenNo,
      prompt,
      ...(typeof ratio === "string" && ratio ? { ratio } : {}),
    });
  }
  return out;
}

/** 推荐 chip 验型 —— 形状/枚举不对就当没有(坏 chip 比没 chip 更糟:它在替 AI 说谎)。 */
const RECO_TONES = new Set([
  "premium_minimal",
  "vivid_promo",
  "warm_life",
  "tech_cool",
  "fresh_clean",
]);
const RECO_LIGHTINGS = new Set(["studio", "natural", "dramatic", "soft"]);
const RECO_MATERIALS = new Set(["matte", "glossy", "frosted", "metallic", "natural"]);

function parseStyleReco(v: unknown): StyleReco | null {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (
    typeof r.tone !== "string" ||
    !RECO_TONES.has(r.tone) ||
    typeof r.lighting !== "string" ||
    !RECO_LIGHTINGS.has(r.lighting) ||
    typeof r.material !== "string" ||
    !RECO_MATERIALS.has(r.material)
  ) {
    return null;
  }
  return {
    tone: r.tone as StyleReco["tone"],
    lighting: r.lighting as StyleReco["lighting"],
    material: r.material as StyleReco["material"],
    reason: typeof r.reason === "string" ? r.reason.slice(0, 60) : "",
  };
}

/**
 * 出图快照验型 —— 形状不对就当**没出过图**(返回 undefined)。
 *
 * ★宁可不提示,也不能凭一份坏快照说「你这块改过了」:
 *   那会把用户骗去花一笔单块出图的钱。误报的代价是真金白银。
 */
function parseGenerated(v: unknown): GeneratedSnapshot | undefined {
  if (!v || typeof v !== "object") return undefined;
  const g = v as Record<string, unknown>;
  if (typeof g.taskId !== "number" || !Number.isFinite(g.taskId)) return undefined;
  if (!g.blocks || typeof g.blocks !== "object" || Array.isArray(g.blocks)) return undefined;
  const blocks: Record<string, string> = {};
  for (const [k, val] of Object.entries(g.blocks as Record<string, unknown>)) {
    if (typeof val === "string") blocks[k] = val;
  }
  return { taskId: g.taskId, blocks };
}

/**
 * 草稿里的出处逐条验型 —— **单条坏只丢那一条**,不整包丢弃。
 *
 * 口径与 `comprehend.parseComprehendResult` 同源:坏骨架整份丢,单条坏丢单条。
 * 出处丢了只是少个徽章,把整份草稿判废会让用户的稿子凭空消失,后者严重得多。
 */
function parseProvenanceList(v: unknown): ProvenanceEntry[] {
  if (!Array.isArray(v)) return [];
  return (v as unknown[]).flatMap((x) => {
    if (!x || typeof x !== "object") return [];
    const e = x as Record<string, unknown>;
    if (typeof e.field !== "string" || typeof e.from !== "string") return [];
    return [{ field: e.field, value: typeof e.value === "string" ? e.value : "", from: e.from }];
  });
}

/** 编辑器可换排版:同 primitive 的 styleTag 白名单(换排版不换原语,内容尽量保留)。 */
export function styleTagsForPrimitive(primitive: LongImageBlockForm["primitive"]): string[] {
  return Object.entries(STYLE_META)
    .filter(([, m]) => m.primitive === primitive)
    .map(([k]) => k);
}
